import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import {
  getCertificationTypesRequest,
  getInspectionServicesRequest,
  getInsuranceTypesRequest,
} from "../../../../../../redux/inspection-firms/slice";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import Loading from "../../../../../../components/loading/loading";
import TextInput from "../../../../../../components/textinput/textinput";
import { updateFormRequest } from "../../../../../../utils/APICalls";
import {
  formTemplateRestrictionOptions,
  photoResolutions,
} from "../../../../../../utils/Constant";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getFormDetailsRequest } from "../../../../../../redux/clients/slice";
import Attachments from "../add-form/attachments";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

type FormValues = {
  formName: string;
  category: string;
  type: string;
  availableOutputs: { excel: boolean; pdf: boolean };
  clientFee: string;
  inspectorFee: string;
  photoResolution: string;
  secondQC: boolean;
  attachments: any[];
};

const ValiditionSchema = yup.object({
  formName: yup.string().required("This field can't be empty"),
  category: yup.string().required("This field can't be empty"),
  type: yup.string().required("This field can't be empty"),
  clientFee: yup
    .number()
    .required("This field can't be empty")
    .positive("Amount must be greater than 0"),
  inspectorFee: yup
    .number()
    .required("This field can't be empty")
    .positive("Amount must be greater than 0"),
  attachments: yup.array(),
});

export default function EditForm({ showDialog, closeDialog, data }: props) {
  const [category, setCategory] = useState<any[]>([]);
  const [type, setType] = useState<any[]>([]);
  const [availableOutputsError, setAvailableOutputsError] = useState(false);
  const [restrictions, setRestrictions] = useState<any[]>([]);
  const [backgroundCheck, setBackgroundCheck] = useState("true");
  const [insurances, setInsurances] = useState<string[]>([]);
  const [certifications, setCertifications] = useState<string[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();

  const { inspectionServices, insuranceTypes, certificationTypes, loading } =
    useSelector((state: any) => state.inspectionFirms);

  useEffect(() => {
    if (inspectionServices.length === 0)
      dispatch(getInspectionServicesRequest());
    if (insuranceTypes.length === 0) dispatch(getInsuranceTypesRequest());
    if (certificationTypes.length === 0)
      dispatch(getCertificationTypesRequest());
  }, []); // eslint-disable-line

  useEffect(() => {
    if (inspectionServices && inspectionServices.length > 0) {
      const uniqueCategories = inspectionServices.reduce(
        (acc: any[], item: any) => {
          if (!acc.includes(item.category)) {
            acc.push(item.category);
          }
          return acc;
        },
        []
      ).sort();
      setCategory(uniqueCategories);
    }

    if (data) {
      const filterTypes = inspectionServices.filter(
        (service: any) => service.category === data.inspectionTypeCategory
      );
      setType(filterTypes);
    }
  }, [data, inspectionServices]);

  useEffect(() => {
    if (data) {
      const filterTypes = inspectionServices.filter(
        (service: any) => service.category === data.inspectionTypeCategory
      );
      setType(filterTypes);

      if (data.restrictions) {
        if (data.restrictions.hasOwnProperty("backgroundCheckDone")) {
          setRestrictions((prev) => [
            ...prev,
            { type: "Background Check Completed" },
          ]);
          setBackgroundCheck(String(data.restrictions.backgroundCheckDone));
        }
        if (
          data.restrictions?.certifications &&
          data.restrictions?.certifications?.length > 0
        ) {
          setRestrictions((prev) => [...prev, { type: "Certifications" }]);
          setCertifications(data.restrictions.certifications);
        }
        if (
          data.restrictions?.insurances &&
          data.restrictions?.insurances?.length > 0
        ) {
          setRestrictions((prev) => [...prev, { type: "Insurance Required" }]);
          setInsurances(data.restrictions.insurances);
        }
      }
    }
  }, [data]); // eslint-disable-line

  const formik = useFormik<FormValues>({
    initialValues: {
      formName: data?.displayName || "",
      category: data?.inspectionTypeCategory || "",
      type: data?.inspectionType || "",
      availableOutputs: {
        excel: data?.availableOutputs?.includes("Excel") || false,
        pdf: data?.availableOutputs?.includes("PDF") || false,
      },
      clientFee: data?.clientFee || "",
      inspectorFee: data?.inspectorFee || "",
      photoResolution:
        data?.photoResolution || photoResolutions.standardResolution,
      secondQC: data?.requireSecondQc || false,
      attachments: data?.attachments || [],
    },
    validationSchema: ValiditionSchema,
    validate: (values) => {
      if (!values.availableOutputs.excel && !values.availableOutputs.pdf) {
        setAvailableOutputsError(true);
      } else {
        setAvailableOutputsError(false);
      }
    },
    onSubmit: (values) => {
      if (isRestricionTypeEmpty()) {
        return;
      }
      const backgroundCheckFilter = restrictions.filter(
        (restriction) => restriction.type === "Background Check Completed"
      );
      const certificationFilter = restrictions.filter(
        (restriction) => restriction.type === "Certifications"
      );
      const insuranceFilter = restrictions.filter(
        (restriction) => restriction.type === "Insurance Required"
      );
      if (
        insuranceFilter &&
        insuranceFilter.length > 0 &&
        insurances.length === 0
      ) {
        return;
      }
      if (
        certificationFilter &&
        certificationFilter.length > 0 &&
        certifications.length === 0
      ) {
        return;
      }

      const availableOutputs: string[] = [];
      if (values.availableOutputs.excel) availableOutputs.push("Excel");
      if (values.availableOutputs.pdf) availableOutputs.push("PDF");

      if (availableOutputs.length === 0) {
        return;
      }

      const restrictionsPayload: any = {};
      if (backgroundCheckFilter.length > 0) {
        restrictionsPayload.backgroundCheckDone =
          backgroundCheck === "true" ? true : false;
      }
      if (insuranceFilter.length > 0) {
        restrictionsPayload.insurances = insurances;
      }
      if (certificationFilter.length > 0) {
        restrictionsPayload.certifications = certifications;
      }

      let deleteAttachmentKeys: any[] = [];
      const availableAttachmentKeys = data?.attachments.map(
        (att: any) => att?.attachmentKey
      );
      const filterAttachmentKeys = values.attachments.map(
        (att) => att.attachmentKey
      );
      const attachmentKeys = filterAttachmentKeys.filter(
        (key) => !availableAttachmentKeys.includes(key)
      );
      if (filterAttachmentKeys && filterAttachmentKeys.length === 0) {
        deleteAttachmentKeys = availableAttachmentKeys;
      } else if (filterAttachmentKeys && filterAttachmentKeys.length > 0) {
        deleteAttachmentKeys = availableAttachmentKeys.filter(
          (key: string) => !filterAttachmentKeys.includes(key)
        );
      }

      const payload: any = {
        displayName: values.formName,
        inspectionType: values.type,
        clientFee: values.clientFee,
        inspectorFee: values.inspectorFee,
        availableOutputs: availableOutputs,
        requireSecondQc: values.secondQC,
        photoResolution: values.photoResolution,
        attachmentKeys: attachmentKeys || [],
        attachmentKeysToDelete: deleteAttachmentKeys || [],
      };
      if (Object.keys(restrictionsPayload).length > 0) {
        payload.restrictions = restrictionsPayload;
      }
      updateForm(payload);
    },
  });

  const isRestricionTypeEmpty = () => {
    let isEmpty = false;
    restrictions.forEach((restriction) => {
      if (!restriction.type) {
        isEmpty = true;
      }
    });
    return isEmpty;
  };

  const onCategoryChange = (event: SelectChangeEvent) => {
    formik.handleChange(event);
    formik.setFieldValue("type", null);
    const value = event.target.value;
    const filterTypes = inspectionServices.filter(
      (service: any) => service.category === value
    );
    setType(filterTypes);
  };

  const onRestrictionTypeChange = (e: SelectChangeEvent, index: number) => {
    const allRestrictions = [...restrictions];
    allRestrictions[index].type = e.target.value;
    setRestrictions(allRestrictions);
  };

  const onBackgroundCheckChange = (e: SelectChangeEvent) => {
    setBackgroundCheck(e.target.value);
  };

  const onInsuranceChange = (event: SelectChangeEvent<typeof insurances>) => {
    const { value }: any = event.target;
    setInsurances(value);
  };

  const onInsuranceDelete = (option: string) => {
    setInsurances((prev) => prev.filter((value) => value !== option));
  };

  const onCertificationChange = (
    event: SelectChangeEvent<typeof certifications>
  ) => {
    const { value }: any = event.target;
    setCertifications(value);
  };

  const onCertificationDelete = (option: string) => {
    setCertifications((prev) => prev.filter((value) => value !== option));
  };

  const addRestictrionClick = () => {
    setRestrictions((prev) => [...prev, { type: "" }]);
  };

  const checkDuplicate = (option: string) => {
    let isSelected = false;
    restrictions.forEach((restriction) => {
      if (restriction.type === option) {
        isSelected = true;
      }
    });
    return isSelected;
  };

  const deleteRestictrionClick = (index: number) => {
    const allRestrictions = [...restrictions];
    allRestrictions.splice(index, 1);
    setRestrictions(allRestrictions);
  };

  const getInsuranceDisplayName = (insuranceType: string) => {
    const filterUser = insuranceTypes?.filter(
      (insurance: any) => insurance.insuranceType === insuranceType
    );
    return filterUser && filterUser.length > 0 && filterUser[0].displayName;
  };

  const getCertificationDisplayName = (certificationType: string) => {
    const filterUser = certificationTypes?.filter(
      (certification: any) =>
        certification.certificationType === certificationType
    );
    return filterUser && filterUser.length > 0 && filterUser[0].displayName;
  };

  const updateForm = async (payload: any) => {
    setShowLoading(true);
    const response = await updateFormRequest(data?.id, payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getFormDetailsRequest({ id: data?.id }));
      closeDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <DialogContainer
      title="Form Settings"
      showDialog={showDialog}
      closeDialog={closeDialog}
      buttonText={"Save"}
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt">Form Name</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <TextInput
              sx={{ width: "100% !important" }}
              className="field-input"
              id="formName"
              name="formName"
              variant="outlined"
              value={formik.values.formName}
              onChange={formik.handleChange}
              error={formik.touched.formName && Boolean(formik.errors.formName)}
              helperText={formik.touched.formName && formik.errors.formName}
            />
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Category</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              sx={{ width: "100% !important" }}
              error={formik.touched.category && Boolean(formik.errors.category)}
            >
              <Select
                id="category"
                name="category"
                value={formik.values.category}
                onChange={onCategoryChange}
              >
                {category.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
              {Boolean(formik.errors.category) && formik.touched.category && (
                <FormHelperText>{formik.errors.category}</FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Type</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              sx={{ width: "100% !important" }}
              error={formik.touched.type && Boolean(formik.errors.type)}
              disabled={type.length === 0}
            >
              <Select
                id="type"
                name="type"
                value={formik.values.type}
                onChange={formik.handleChange}
              >
                {type.map((item) => (
                  <MenuItem
                    key={item.inspectionType}
                    value={item.inspectionType}
                  >
                    {item.displayName}
                  </MenuItem>
                ))}
              </Select>
              {Boolean(formik.errors.type) && formik.touched.type && (
                <FormHelperText>{formik.errors.type}</FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Available Output</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    name="availableOutputs.excel"
                    checked={formik.values.availableOutputs.excel}
                    onChange={formik.handleChange}
                  />
                }
                label="Excel"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="availableOutputs.pdf"
                    checked={formik.values.availableOutputs.pdf}
                    onChange={formik.handleChange}
                  />
                }
                label="PDF"
              />
              {availableOutputsError && (
                <Typography sx={{ fontSize: 12, ml: 2, mt: 0.5 }} color="error">
                  Please select at least one output format
                </Typography>
              )}
            </FormGroup>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Attachments</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Typography sx={{ fontStyle: "italic" }}>
              The following atachments will be included for every order placed
              using this form. They will be visible to inspectors...fpo
            </Typography>
            <Attachments formik={formik} />
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Standard Client Fee</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              fullWidth
              error={
                formik.touched.clientFee && Boolean(formik.errors.clientFee)
              }
            >
              <InputLabel htmlFor="outlined-client-fee-amount"></InputLabel>
              <OutlinedInput
                sx={{
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    {
                      display: "none",
                      margin: 0,
                    },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                }}
                id="outlined-client-fee-amount"
                name="clientFee"
                type="number"
                startAdornment={
                  <InputAdornment position="start">
                    <AttachMoneyOutlinedIcon
                      sx={{ color: "rgba(0, 0, 0, 0.87)", fontSize: 20 }}
                    />
                  </InputAdornment>
                }
                value={formik.values.clientFee}
                onChange={formik.handleChange}
              />
              {Boolean(formik.errors.clientFee) && formik.touched.clientFee && (
                <FormHelperText>{formik.errors.clientFee}</FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Standard Inspector Fee</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              fullWidth
              error={
                formik.touched.inspectorFee &&
                Boolean(formik.errors.inspectorFee)
              }
            >
              <InputLabel htmlFor="outlined-inspector-fee-amount"></InputLabel>
              <OutlinedInput
                sx={{
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    {
                      display: "none",
                      margin: 0,
                    },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                }}
                id="outlined-inspector-fee-amount"
                name="inspectorFee"
                type="number"
                startAdornment={
                  <InputAdornment position="start">
                    <AttachMoneyOutlinedIcon
                      sx={{ color: "rgba(0, 0, 0, 0.87)", fontSize: 20 }}
                    />
                  </InputAdornment>
                }
                value={formik.values.inspectorFee}
                onChange={formik.handleChange}
              />
              {Boolean(formik.errors.inspectorFee) &&
                formik.touched.inspectorFee && (
                  <FormHelperText>{formik.errors.inspectorFee}</FormHelperText>
                )}
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Box
            sx={{
              width: 192,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
              Photo Resolution
            </Typography>
            <Tooltip
              title="Original resolution should only be selected if a form absolutely requires full size images as a final output and may incur additional costs."
              arrow
              placement="top"
            >
              <IconButton sx={{ p: 0, mr: 2 }}>
                <InfoOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <RadioGroup>
              <FormControlLabel
                control={
                  <Radio
                    name="photoResolution"
                    checked={
                      formik.values.photoResolution ===
                      photoResolutions.standardResolution
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        formik.setFieldValue(
                          "photoResolution",
                          photoResolutions.standardResolution
                        );
                      }
                    }}
                  />
                }
                label="Standard Resolution"
              />
              <FormControlLabel
                control={
                  <Radio
                    name="photoResolution"
                    checked={
                      formik.values.photoResolution ===
                      photoResolutions.originalResolution
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        formik.setFieldValue(
                          "photoResolution",
                          photoResolutions.originalResolution
                        );
                      }
                    }}
                  />
                }
                label="Original Resolution"
              />
            </RadioGroup>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Standard Restrictions</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Box>
              {restrictions?.length === 0 && (
                <Typography>No Restrictions</Typography>
              )}
              {restrictions?.map((restriction, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: index === 0 ? 0 : 2,
                    position: "relative",
                  }}
                >
                  <FormControl
                    sx={{ flex: 1 }}
                    error={!restriction.type && formik.submitCount > 0}
                  >
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={restriction.type}
                      onChange={(e) => onRestrictionTypeChange(e, index)}
                    >
                      {formTemplateRestrictionOptions.map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          disabled={checkDuplicate(option)}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {!restriction.type && formik.submitCount > 0 && (
                      <Typography
                        sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                        color="error"
                      >
                        This field can't be empty
                      </Typography>
                    )}
                  </FormControl>
                  {restriction.type === "Background Check Completed" && (
                    <>
                      <FormControl sx={{ flex: 2, ml: 2, mr: 6 }}>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={backgroundCheck}
                          onChange={onBackgroundCheckChange}
                        >
                          <MenuItem value={"true"}>True</MenuItem>
                          <MenuItem value={"false"}>False</MenuItem>
                        </Select>
                        {!backgroundCheck && formik.submitCount > 0 && (
                          <Typography
                            sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                            color="error"
                          >
                            This field can't be empty
                          </Typography>
                        )}
                      </FormControl>
                    </>
                  )}

                  {restriction.type === "Insurance Required" && (
                    <>
                      <FormControl
                        sx={{ flex: 2, ml: 2, mr: 6 }}
                        error={
                          insurances &&
                          insurances.length === 0 &&
                          formik.submitCount > 0
                        }
                      >
                        <Select
                          labelId="demo-multiple-chip-label"
                          id="demo-multiple-chip"
                          multiple
                          value={insurances}
                          onChange={onInsuranceChange}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => (
                                <Chip
                                  color="primary"
                                  variant="outlined"
                                  onDelete={() => onInsuranceDelete(value)}
                                  key={value}
                                  label={getInsuranceDisplayName(value)}
                                  deleteIcon={
                                    <IconButton
                                      onMouseDown={(e) => e.stopPropagation()}
                                      size="small"
                                      sx={{ padding: 0.5 }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  }
                                />
                              ))}
                            </Box>
                          )}
                        >
                          {insuranceTypes.map((option: any) => (
                            <MenuItem
                              key={option.insuranceType}
                              value={option.insuranceType}
                            >
                              {option.displayName}
                            </MenuItem>
                          ))}
                        </Select>
                        {insurances &&
                          insurances.length === 0 &&
                          formik.submitCount > 0 && (
                            <FormHelperText>
                              This field can't be empty
                            </FormHelperText>
                          )}
                      </FormControl>
                    </>
                  )}

                  {restriction.type === "Certifications" && (
                    <>
                      <FormControl
                        sx={{ flex: 2, ml: 2, mr: 6 }}
                        error={
                          certifications &&
                          certifications.length === 0 &&
                          formik.submitCount > 0
                        }
                      >
                        <Select
                          labelId="demo-multiple-chip-label"
                          id="demo-multiple-chip"
                          multiple
                          value={certifications}
                          onChange={onCertificationChange}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => (
                                <Chip
                                  color="primary"
                                  variant="outlined"
                                  onDelete={() => onCertificationDelete(value)}
                                  key={value}
                                  label={getCertificationDisplayName(value)}
                                  deleteIcon={
                                    <IconButton
                                      onMouseDown={(e) => e.stopPropagation()}
                                      size="small"
                                      sx={{ padding: 0.5 }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  }
                                />
                              ))}
                            </Box>
                          )}
                        >
                          {certificationTypes.map((option: any) => (
                            <MenuItem
                              key={option.certificationType}
                              value={option.certificationType}
                            >
                              {option.displayName}
                            </MenuItem>
                          ))}
                        </Select>
                        {certifications &&
                          certifications.length === 0 &&
                          formik.submitCount > 0 && (
                            <FormHelperText>
                              This field can't be empty
                            </FormHelperText>
                          )}
                      </FormControl>
                    </>
                  )}

                  {restriction.type === "" && (
                    <Box sx={{ flex: 2, marginRight: 8 }}></Box>
                  )}

                  <IconButton
                    sx={{ position: "absolute", right: 0 }}
                    aria-label="delete"
                    onClick={() => deleteRestictrionClick(index)}
                  >
                    <DeleteOutlineOutlinedIcon style={{ color: "#D32F2F" }} />
                  </IconButton>
                </Box>
              ))}

              {restrictions?.length < 3 && (
                <Box mt={2}>
                  <Button
                    variant="text"
                    startIcon={<AddIcon />}
                    onClick={addRestictrionClick}
                  >
                    ADD RESTRICTION
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Always Require 2nd QC</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    name="secondQC"
                    checked={formik.values.secondQC}
                    onChange={formik.handleChange}
                  />
                }
                label={formik.values.secondQC ? "Yes" : "No"}
              />
            </FormGroup>
          </Box>
        </Box>
      </form>
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      {(showLoading || loading) && <Loading />}
    </DialogContainer>
  );
}
